import BaseObject from "../models/BaseObject";
import UndoStep from "./UndoStep";
import Logger from "../Logger";
import BaseModel from "../models/BaseModel";
import BaseModelMap from "../models/BaseModelMap";
import { showToast } from "../components/Toasts";

const logger = new Logger();

export type UndoDialogStep = { commit(): Promise<void>; rollback(): Promise<void> };

export default class UndoManager extends BaseObject {
  static init() {
    const undoManager = globalThis.state.undoManager;
    BaseObject.setBeforeChangeCallback((object) => undoManager.captureBeforeChange(object));
    BaseObject.setChangedCallback((object) => undoManager.captureChanged(object));
    BaseObject.setBeforeDeleteCallback((object) => {
      if (object.isBaseModel) undoManager.captureDeletedObject(object as BaseModel);
    });
    BaseObject.setAfterCreateNewCallback((object) => {
      if (object.isBaseModel) undoManager.captureNewObject(object as BaseModel);
    });
    BaseModelMap.setBeforeAddCallback((collection, object, index) =>
      undoManager.captureAddToCollection(collection, object, index),
    );
    BaseModelMap.setBeforeRemoveCallback((collection, object, index) =>
      undoManager.captureRemoveFromCollection(collection, object, index),
    );
  }

  public get className(): string {
    return "UndoManager"; // no itx
  }

  private currentUndoStep: UndoStep | undefined = undefined;
  private nesting = 0;
  private undoSteps = Array<UndoStep>();
  private redoSteps = Array<UndoStep>();
  private implicitStep = false;
  private replaying = false;
  private implicitCloseTimer: ReturnType<typeof setTimeout> | undefined;
  private pendingAsyncWork = new Set<Promise<unknown>>();
  private finishRequested = false;
  private dialogStep: UndoStep | undefined;

  public get canUndo(): boolean {
    return !this.dialogStep && this.pendingAsyncWork.size === 0 && this.undoSteps.length > 0;
  }
  public get canRedo(): boolean {
    return !this.dialogStep && this.pendingAsyncWork.size === 0 && this.redoSteps.length > 0;
  }

  get isCompletingInteraction(): boolean {
    return this.pendingAsyncWork.size > 0;
  }

  get hasUndoEntries(): boolean {
    return (
      this.undoSteps.length > 0 ||
      this.redoSteps.length > 0 ||
      Boolean(this.currentUndoStep && !this.currentUndoStep.isEmpty)
    );
  }

  disableAddStep = false;

  get isReplaying(): boolean {
    return this.replaying;
  }

  addUndoStep(undoStep: UndoStep) {
    if (this.disableAddStep) return;

    undoStep.filterTemporaryObjects();
    if (undoStep.isEmpty) return;

    this.undoSteps.unshift(undoStep);

    if (!undoStep.selectedObjectId) {
      undoStep.selectedObjectId = state.selectionManager.selectedObject?.id;
    }

    // clear redo
    this.redoSteps = Array<UndoStep>();
    this.raiseOnChange();
  }

  get undoStepIsOpen(): boolean {
    return this.nesting > 0;
  }

  runAsSingleStep<T>(description: string, callback: () => T): T {
    if (this.dialogStep) return callback();
    this.closeImplicitStep();
    if (this.currentUndoStep) {
      throw new Error("Cannot start an undo step while asynchronous work from another interaction is pending.");
    }
    this.currentUndoStep = this.createUndoStep();
    this.currentUndoStep.description = description;
    this.nesting = 1;
    this.implicitStep = false;
    this.finishRequested = false;

    this.currentUndoStep.selectedObjectId = state.selectionManager.selectedObject?.id;

    try {
      return callback();
    } finally {
      this.nesting = 0;
      this.requestFinishCurrentStep();
    }
  }

  /** Reverts the tentative edits in a synchronous step, then continues with a fresh step. */
  rollbackCurrentStep(): void {
    const step = this.currentUndoStep;
    if (!step || this.nesting !== 1 || this.dialogStep || this.pendingAsyncWork.size)
      throw new Error("Cannot roll back outside a synchronous undo step.");
    this.replay(() => step.undo());
    this.restoreSelection(step);
    this.currentUndoStep = this.createUndoStep();
    this.currentUndoStep.description = step.description;
  }

  /** Owns creation and all modal edits until the user explicitly accepts or cancels. */
  beginDialogStep(description: string): UndoDialogStep {
    if (this.dialogStep) throw new Error("A creation dialog is already active.");
    this.closeImplicitStep();
    if (this.currentUndoStep || this.pendingAsyncWork.size) throw new Error("Another interaction is still completing.");
    const step = this.createUndoStep();
    step.description = description;
    this.currentUndoStep = this.dialogStep = step;
    this.nesting = 1;
    this.implicitStep = false;
    this.finishRequested = false;
    this.raiseOnChangeUI();
    let finishing: Promise<void> | undefined;
    const finish = (accept: boolean): Promise<void> => finishing ??= (async () => {
      while (this.pendingAsyncWork.size) await Promise.allSettled([...this.pendingAsyncWork]);
      if (this.dialogStep !== step) return;
      this.dialogStep = undefined;
      this.currentUndoStep = undefined;
      this.nesting = 0;
      this.finishRequested = false;
      if (accept) {
        this.addUndoStep(step);
      } else {
        this.replay(() => step.undo());
        this.restoreSelection(step);
      }
      this.raiseOnChangeUI();
    })();
    return { commit: () => finish(true), rollback: () => finish(false) };
  }

  /**
   * Associates asynchronous model work with the currently active undo step.
   * If no step exists, the work creates an implicit step. The step cannot be
   * committed until every registered promise has settled.
   */
  trackAsyncWork<T>(promise: Promise<T>): Promise<T> {
    if (!this.shouldTrackAsyncWork()) return promise;

    this.ensureStep();
    const trackedPromise = promise as Promise<unknown>;
    if (this.pendingAsyncWork.has(trackedPromise)) return promise;

    this.pendingAsyncWork.add(trackedPromise);
    this.raiseOnChangeUI();
    this.scheduleImplicitClose();

    const settled = () => {
      this.pendingAsyncWork.delete(trackedPromise);
      if (this.finishRequested && this.pendingAsyncWork.size === 0) {
        this.finishCurrentStep();
      } else {
        this.raiseOnChangeUI();
      }
    };
    trackedPromise.then(settled, settled);

    return promise;
  }

  logUndoSteps() {
    const stepRows = this.undoSteps.map((step, stepIndex) => ({
      undoStep: String(stepIndex + 1),
      description: step.description,
    }));

    if (this.currentUndoStep && !this.currentUndoStep.isEmpty) {
      stepRows.push({
        undoStep: "pending",
        description: this.currentUndoStep.description,
      });
    }

    const rows = this.undoSteps.flatMap((step, stepIndex) =>
      step.getDebugInfo().map((operation, operationIndex) => ({
        undoStep: String(stepIndex + 1),
        stepOperation: operationIndex + 1,
        ...operation,
      })),
    );

    if (this.currentUndoStep && !this.currentUndoStep.isEmpty) {
      rows.push(
        ...this.currentUndoStep.getDebugInfo().map((operation, operationIndex) => ({
          undoStep: "pending",
          stepOperation: operationIndex + 1,
          ...operation,
        })),
      );
    }

    console.log(
      `Undo stack: ${this.undoSteps.length} step(s), newest first; ` +
        `${this.pendingAsyncWork.size} pending async task(s); finish requested: ${this.finishRequested}`,
    );
    console.table(stepRows);
    console.table(rows);
  }

  get undoStepSummaries(): ReadonlyArray<{ number: number; name: string; timestamp: number }> {
    return this.undoSteps.map((step, index) => ({
      number: index + 1,
      name: step.description || "Änderung",
      timestamp: step.timestamp,
    }));
  }

  undo = () => {
    if (this.dialogStep) return;
    if (this.pendingAsyncWork.size > 0) {
      void showToast({ text: "Estada arbeitet noch. Bitte klicken Sie später noch einmal auf Undo." });
      return;
    }
    const last = this.undoSteps.shift();
    if (last) {
      this.replay(() => last.undo());
      this.restoreSelection(last);
      this.redoSteps.unshift(last);
    }
    this.raiseOnChange();
  };

  redo = () => {
    if (this.dialogStep) return;
    if (this.pendingAsyncWork.size > 0) {
      void showToast({ text: "Estada arbeitet noch. Bitte klicken Sie später noch einmal auf Undo." });
      return;
    }
    const last = this.redoSteps.shift();
    if (last) {
      this.replay(() => last.redo());
      this.undoSteps.unshift(last);
    }
    this.raiseOnChange();
  };

  clear() {
    if (this.implicitCloseTimer) clearTimeout(this.implicitCloseTimer);
    this.implicitCloseTimer = undefined;
    this.undoSteps = Array<UndoStep>();
    this.redoSteps = Array<UndoStep>();
    this.currentUndoStep = undefined;
    this.dialogStep = undefined;
    this.nesting = 0;
    this.implicitStep = false;
    this.pendingAsyncWork.clear();
    this.finishRequested = false;
    this.raiseOnChangeUI();
  }

  captureBeforeChange(object: BaseObject) {
    if (!this.shouldCapture(object)) return;
    const step = this.ensureStep();
    step.captureObject(object);
  }

  captureChanged(_object: BaseObject) {
    this.scheduleImplicitClose();
  }

  captureNewObject(object: BaseModel) {
    if (!this.shouldCaptureNewObject(object)) return;
    this.ensureStep().captureNewObject(object);
    this.scheduleImplicitClose();
  }

  captureDeletedObject(object: BaseModel) {
    if (!this.shouldCapture(object)) return;
    this.ensureStep().captureDeletedObject(object);
    this.scheduleImplicitClose();
  }

  captureAddToCollection(collection: BaseModelMap<BaseModel>, object: BaseModel, index: number) {
    if (!this.shouldCapture(collection) || !object.isPersistent || object.isTemporary) return;
    this.ensureStep().captureAddToCollection(collection, object, index);
    this.scheduleImplicitClose();
  }

  captureRemoveFromCollection(collection: BaseModelMap<BaseModel>, object: BaseModel, index: number) {
    if (!this.shouldCapture(collection) || !object.isPersistent || object.isTemporary) return;
    this.ensureStep().captureRemoveFromCollection(collection, object, index);
    this.scheduleImplicitClose();
  }

  private shouldCapture(object: BaseObject): boolean {
    const state = globalThis.state;
    return (
      object.monitorForUndo &&
      !this.isTemporaryObject(object) &&
      !this.disableAddStep &&
      !this.replaying &&
      object.isPersistent &&
      !state?.isLoadingFromEstadaStorage &&
      !state?.suppressUndoObservation
    );
  }

  private shouldCaptureNewObject(object: BaseModel): boolean {
    const state = globalThis.state;
    return (
      !this.disableAddStep &&
      !this.replaying &&
      !object.isBaseModelMap &&
      !object.isTemporary &&
      object.isPersistent &&
      !state?.isLoadingFromEstadaStorage &&
      !state?.suppressUndoObservation
    );
  }

  private isTemporaryObject(object: BaseObject): boolean {
    return object.isBaseModel && Boolean((object as BaseModel).isTemporary);
  }

  private shouldTrackAsyncWork(): boolean {
    const state = globalThis.state;
    return (
      !this.disableAddStep && !this.replaying && !state?.isLoadingFromEstadaStorage && !state?.suppressUndoObservation
    );
  }

  private ensureStep(): UndoStep {
    if (!this.currentUndoStep) {
      this.currentUndoStep = this.createUndoStep();
      this.nesting = 1;
      this.implicitStep = true;
    }
    return this.currentUndoStep;
  }

  private createUndoStep(): UndoStep {
    return new UndoStep({ selectedObject: globalThis.state?.selectionManager?.selectedObject });
  }

  private restoreSelection(step: UndoStep) {
    const selectedObject = globalThis.state.objectRegistry.getObject<BaseModel>(step.selectedObjectId);
    globalThis.state.selectionManager.selectedObject = selectedObject;
  }

  private closeImplicitStep() {
    if (this.dialogStep) return;
    if (this.implicitCloseTimer) clearTimeout(this.implicitCloseTimer);
    this.implicitCloseTimer = undefined;
    this.implicitStep = false;
    this.nesting = 0;

    this.requestFinishCurrentStep();
  }

  private requestFinishCurrentStep() {
    this.nesting = 0;
    this.implicitStep = false;
    this.finishRequested = true;
    if (this.pendingAsyncWork.size > 0) return;

    this.finishCurrentStep();
  }

  private finishCurrentStep() {
    const step = this.currentUndoStep;
    this.currentUndoStep = undefined;
    this.pendingAsyncWork.clear();
    this.finishRequested = false;
    if (step && !step.isEmpty) {
      this.addUndoStep(step);
    } else {
      this.raiseOnChangeUI();
    }
  }

  private scheduleImplicitClose() {
    if (!this.implicitStep) return;
    if (this.implicitCloseTimer) clearTimeout(this.implicitCloseTimer);
    this.implicitCloseTimer = setTimeout(() => this.closeImplicitStep(), 500);
  }

  userInteractionEnded(description?: string) {
    if (this.dialogStep) return;
    if (description && this.currentUndoStep) this.currentUndoStep.description = description;
    this.closeImplicitStep();
  }

  private replay(callback: () => void) {
    this.replaying = true;
    try {
      callback();
    } finally {
      this.replaying = false;
    }
  }
}
